import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent, TeamSectionItemType } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import OwnerSelectComponent from 'webapp/components/owner-select';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagContainerComponent from 'webapp/components/tag-container';
import TeamsViewSectionRestrictedIconComponent from 'webapp/components/teams/view/section/restricted-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Action as BoardLoopAction } from 'webapp/models-custom/board-loop';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type TeamSectionItem from 'webapp/models-custom/team-section-item';

import type {
	TrackingOriginType,
	TrackingOriginTypeForDuplicate,
	TrackingOriginTypeForPreview,
} from 'webapp/services/model-manager/board-loops';
import type { Action as TeamAction } from 'webapp/services/team';

interface Args {
	model: BoardLoop;
	hideSort?: boolean;
	teamSectionItem?: TeamSectionItem;
	removeFromSection?: (item: TeamSectionItem) => void;
	isRestricted?: boolean;
	checkVisibility?: boolean;
}

type Action = BoardLoopAction | TeamAction;
export type Option = PopupListItem<Action>;

export default class BoardLoopsGridItemComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('team') declare teamService: Service.Team;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	@tracked isEditingName = false;
	@tracked editedName = '';
	@tracked overlayActive = false;
	@tracked isTitleTooLong = false;
	@tracked isVisibleOnScreen = false;

	trackOrigin: TrackingOriginType = 'home';

	get additionalOptions(): Option[] {
		const shownOutsideDropdown = ['openPreview', 'edit', 'share', 'toggleFavorite', 'restore'],
			teamSectionItemOptions = this.teamSectionItemOptions,
			modelAdditionalOptions = this.args.model
				.getAdditionalOptions(true, false, this.isTeamSectionItem)
				.filter((item) => {
					return item.action && !shownOutsideDropdown.includes(item.action);
				}) as Option[];

		modelAdditionalOptions.push(...teamSectionItemOptions);

		return modelAdditionalOptions;
	}

	get isTeamSectionItem(): boolean {
		return !!this.args.teamSectionItem && !!this.args.removeFromSection;
	}

	get teamSectionItemOptions(): Option[] {
		return this.teamService.getTeamSectionItemOptions(this.isTeamSectionItem);
	}

	get canCurrentUserEdit(): boolean {
		const canCurrentUserEdit = this.args.model.canCurrentUserEdit,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEdit
			: canCurrentUserEdit;
	}

	get canCurrentUserEditSomeBoard(): boolean {
		const canCurrentUserEditSomeBoard = this.args.model.canCurrentUserEditSomeBoard,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEditSomeBoard
			: canCurrentUserEditSomeBoard;
	}

	get showActions(): boolean {
		const teamSectionItem = this.args.teamSectionItem;
		return teamSectionItem ? teamSectionItem.isCurrentUserMember : true;
	}

	get checkVisibility() {
		return !!this.args.checkVisibility;
	}

	get isDeleted(): boolean {
		return !!this.args.model.deleted;
	}

	get showDragHandle(): boolean {
		const isDragHandleShown = !this.currentUserService.isDemo && !this.isDeleted,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && isDragHandleShown
			: isDragHandleShown;
	}

	checkIfTitleTooLong(node: HTMLElement) {
		const containerWidth = node
				? Math.round(
						(node.querySelector('.info label')?.parentNode as HTMLElement)
							?.offsetWidth || 0,
					)
				: 0,
			textWidth = node
				? Math.round(
						node.querySelector<HTMLElement>('.info label strong')?.offsetWidth || 0,
					)
				: 0,
			paddingForIconAndEllipsisWidth = 20;

		this.isTitleTooLong = textWidth + paddingForIconAndEllipsisWidth >= containerWidth;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openAdditionalOptions(e: PointerEvent): void {
		if (this.overlayActive) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.context');

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createListPopup(attachTo, undefined, 'board-options');

		if (popup) {
			popup.open({
				itemsList: this.additionalOptions,
				hideDropdownSearch: true,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.overlayActive = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.overlayActive = false;
				}
			});
		}
	}

	@action
	scheduleCheckIfTitleTooLong(node: HTMLElement) {
		schedule('afterRender', this, this.checkIfTitleTooLong, node);
	}

	@action
	openPreview() {
		this.mmBoardLoopsService.openPreviewPopup(
			this.args.model,
			this.trackOrigin as TrackingOriginTypeForPreview,
		);
	}

	@action
	edit() {
		this.mmBoardLoopsService.openEditPopup(this.args.model, this.trackOrigin);
	}

	@action
	toggleFavorite() {
		this.mmBoardLoopsService.toggleFavorite(this.args.model);
	}

	@action
	share() {
		this.mmBoardLoopsService.openSharePopup(this.args.model, this.trackOrigin);
	}

	@action
	openFullscreenView() {
		this.mmBoardLoopsService.openFullscreenView(this.args.model);
	}

	@action
	duplicate() {
		void this.mmBoardLoopsService.duplicate(
			this.args.model,
			this.trackOrigin as TrackingOriginTypeForDuplicate,
		);
	}

	@action
	openInDesigner() {
		this.mmBoardLoopsService.openInDesigner(this.args.model);
	}

	@action
	addToTeam() {
		const itemId = this.args.model.id;

		if (itemId) {
			this.mmTeamSectionsService.openAddPopup({
				objectId: itemId,
				objectType: TeamSectionItemType.DATABOARD_LOOP,
			});
		}
	}

	@action
	createNotification() {
		this.mmBoardLoopsService.createAndOpenNewNotificationForModel(this.args.model, 'loops');
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.args.model,
		});
	}

	@action
	openPublicTemplateEditPopup() {
		this.args.model.openPublicTemplateEditPopup();
	}

	@action
	openChangeOwnerPopup() {
		this.args.model.openChangeOwnerPopup();
	}

	@action
	createReport() {
		this.mmReportsService.createFromLoop(this.args.model, 'loops');
	}

	@action
	saveAsTemplate() {
		this.mmBoardLoopsService.openAccountTemplateEditPopup(this.args.model, 'board-loops');
	}

	@action
	deleteLoop() {
		this.mmBoardLoopsService.delete(this.args.model);
	}

	@action
	restore() {
		void this.mmBoardLoopsService.restore(this.args.model);
	}

	@action
	deletePermanently() {
		this.mmBoardLoopsService.delete(this.args.model, undefined, true);
	}

	@action
	editBoardName() {
		if (this.args.model.canCurrentUserEdit) {
			this.editedName = this.args.model.title;
			this.isEditingName = true;
		}
	}

	@action
	checkBoardName() {
		const editedName = this.editedName.trim();

		if (editedName && editedName !== this.args.model.title) {
			this.args.model.title = editedName;
			this.args.model
				.save()
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
		this.isEditingName = false;
	}

	@action
	removeFromSection() {
		const teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"card"
					"card--preview"
					"flex-item"
					"filter-sort-item"
					(if this.overlayActive "card--show-overlay")
					(if @hideSort "hideSort")
				)
			}}
		>

			<BoardImagePreviewComponent
				@image={{@model.image}}
				@color={{@model.color}}
				@theme={{@model.bgTheme}}
				@isLazyLoaded={{true}}
			/>

			{{#if this.isVisibleOnScreen}}

				<div class="overlay">

					<div class="overlay-bg"></div>

					{{#if this.showDragHandle}}
						<div class="drag" data-id={{@model.id}}>
							<CommonUiIconComponent @name="ArrowsMove" @size="md" />
						</div>
					{{/if}}

					<div class="wrap">
						<div class="actions">
							<div class="actions__top">
								<SubmitButtonComponent
									@text="View"
									@extraClass="previewbutton"
									@onTrigger={{this.openPreview}}
									@noDelay={{true}}
								/>

								{{#if this.canCurrentUserEditSomeBoard}}
									<SubmitButtonComponent
										@text="Edit"
										@icon="Pencil"
										@extraClass="editbutton"
										@onTrigger={{this.openInDesigner}}
										@noDelay={{true}}
										@theme="grey-outlined"
									/>
								{{/if}}
							</div>
							{{#if this.showActions}}
								<div class="actions__bottom">
									{{#if this.canCurrentUserEdit}}
										<SubmitButtonComponent
											@text="Share"
											@icon="Share"
											@onTrigger={{this.share}}
											@noDelay={{true}}
											@theme="grey-outlined"
										/>
									{{/if}}
								</div>
							{{/if}}
						</div>
					</div>

				</div>

				<div class="top">
					<div class="top__left">
						<span>
							<CommonUiIconComponent @name="Loop" @size="md" @class="loop-icon" />
						</span>
					</div>

					{{#if this.showActions}}
						<div class="top__right">
							<div role="button" class="favorite" {{on "click" this.toggleFavorite}}>
								<CommonUiIconComponent
									@name="StarFill"
									@size="sm"
									@class={{unless @model.favorite "gray"}}
								/>
								<ToolTipComponent @text={{@model.favoriteText}} />
							</div>
							{{#if @isRestricted}}
								<TeamsViewSectionRestrictedIconComponent />
							{{/if}}
							<div
								role="button"
								{{on "click" this.openAdditionalOptions}}
								class="context"
							>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						</div>
					{{/if}}
				</div>

				<div class="info">
					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canCurrentUserEdit}}
						/>

						<div class="description">{{@model.boardCountText}}</div>
					</div>
				</div>

				<div class="bottom">
					<OwnerSelectComponent @model={{@model}} @canEdit={{false}} />
					<TagContainerComponent @model={{@model}} @canEdit={{this.canCurrentUserEdit}} />
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged this.checkVisibility}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}

		</li>
	</template>
}
